/**
 * Page-context handshake — pure decision machine.
 *
 * Companion to `reducer.ts` (same hand-rolled `(state, event) → { state,
 * action }` shape, same rationale: the driver owns the real socket/timers,
 * this module owns every policy decision so it can be unit-tested without a
 * WebSocket). Drives the embed panel's half of the two-frame `page_context` /
 * `page_context_ack` handshake (page-context-plan.md §4, §WS-E.4):
 *
 *   1. On every socket open (AFTER the settings frame), if the panel is
 *      holding a page-context payload (from the host's EMBED_IN.PAGE_CONTEXT
 *      postMessage), send a HEAD frame (manifest only) under a FRESH
 *      `request_id` — control frames are not replayed after a drop, so this
 *      re-runs on every reconnect (D3).
 *   2. If a payload arrives from the host WHILE the socket is already open
 *      (a same-panel navigation), send a head frame immediately — this
 *      REPLACES whatever binding is in flight (plan §WS-D.6).
 *   3. The backend's ack says which leg of the handshake this is:
 *        - `cached`    — done; the server already holds every section.
 *        - `send_body` — send a body frame carrying ONLY the listed
 *                        `missing` section ids (the caller/driver looks the
 *                        text up in the payload it's still holding). Can
 *                        also arrive LATER, under the SAME request_id, when
 *                        a section has since expired from the server's
 *                        cache (WS-D.7) — handled identically.
 *        - `stored`    — the body frame was accepted; done.
 *        - `disabled`  — the feature (or Redis) is off; stop for this
 *                        socket, but retry on the NEXT one (a flag can flip
 *                        back on within ~60s — see the plan's RuntimeFlags
 *                        design).
 *        - `rejected`  — the frame failed validation. Unlike `disabled`,
 *                        this is NOT retried on the next socket — the
 *                        payload itself is malformed, and reopening the
 *                        socket doesn't fix that. It clears only when a
 *                        genuinely NEW payload arrives (PAYLOAD_ARRIVED).
 *   4. An ack whose `request_id` doesn't match the one currently in flight
 *      is a LATE ack from a superseded request — ignored (logged), never
 *      allowed to move the state machine.
 *   5. No ack within PAGE_CONTEXT_ACK_TIMEOUT_MS → `unsupported` (an older
 *      backend that doesn't speak this protocol at all just stays silent —
 *      see the plan §4's "old backends" note). Scoped to THIS socket only:
 *      the next reconnect starts fresh (SOCKET_CLOSED resets to idle).
 */

// ── Phase ────────────────────────────────────────────────────────

export const PageContextPhase = {
  /** No handshake in flight (no payload yet, or just reset). */
  IDLE: 'idle',
  /** Head frame sent; awaiting its ack. */
  HEAD_SENT: 'head_sent',
  /** Body frame sent (in response to `send_body`); awaiting its ack. */
  BODY_SENT: 'body_sent',
  /** The current payload is fully resolved server-side (`cached`/`stored`). */
  ACKED: 'acked',
  /** No ack arrived in time — scoped to the CURRENT socket only. */
  UNSUPPORTED: 'unsupported',
  /** The backend rejected the frame — scoped to this PAGE LOAD (does not
   *  retry on reconnect; only a new payload clears it). */
  REJECTED: 'rejected',
} as const;
export type PageContextPhase =
  (typeof PageContextPhase)[keyof typeof PageContextPhase];

/** How long the panel waits for a `page_context_ack` before giving up on
 *  this socket (D-something / plan §WS-E.4). */
export const PAGE_CONTEXT_ACK_TIMEOUT_MS = 5000;

// ── State ────────────────────────────────────────────────────────

export interface PageContextMachineState {
  phase: PageContextPhase;
  /** The request_id of the in-flight (or last-resolved) handshake. Set the
   *  moment a head frame is sent; cleared back to null on SOCKET_CLOSED
   *  (unless the phase is REJECTED — see the file header). */
  requestId: string | null;
  /** Section ids the server most recently asked for (`send_body`'s
   *  `missing`) — kept on state mainly for introspection/debugging; the
   *  driver reads it straight off the SEND_BODY action instead. */
  missing: string[];
}

export const INITIAL_PAGE_CONTEXT_STATE: PageContextMachineState = {
  phase: PageContextPhase.IDLE,
  requestId: null,
  missing: [],
};

// ── Events (inputs) ─────────────────────────────────────────────

/** Mirrors PageContextAckEvent.status (aol_schemas.ws_events). */
export type PageContextAckStatus =
  | 'cached'
  | 'send_body'
  | 'stored'
  | 'disabled'
  | 'rejected';

export type PageContextEvent =
  /** Driver: a socket just opened (settings already sent). `requestId` is
   *  pre-minted by the caller (mirrors reducer.ts's SEND event convention —
   *  this module never mints ids itself, keeping it a pure function of its
   *  inputs). */
  | { type: 'SOCKET_OPENED'; hasPayload: boolean; requestId: string }
  /** Driver: the host posted a page-context payload (initial extract, or a
   *  same-panel navigation replacing the current binding). */
  | { type: 'PAYLOAD_ARRIVED'; socketOpen: boolean; requestId: string }
  /** Driver: a `page_context_ack` frame arrived. */
  | {
      type: 'ACK_RECEIVED';
      requestId: string;
      status: PageContextAckStatus;
      missing?: string[];
      reason?: string | null;
    }
  /** Driver: the ack timer elapsed with nothing resolved. */
  | { type: 'ACK_TIMEOUT' }
  /** Driver: ws.onclose fired (any close — intentional or drop). */
  | { type: 'SOCKET_CLOSED' };

// ── Actions (outputs the driver executes) ──────────────────────

export type PageContextAction =
  /** Send the head frame (manifest only) under `requestId`. */
  | { type: 'SEND_HEAD'; requestId: string }
  /** Send the body frame — the CURRENT payload's `sections`, filtered down
   *  to exactly these ids — under `requestId` (the ORIGINAL request_id;
   *  same value on a later tool-triggered re-ask, per WS-D.7). */
  | { type: 'SEND_BODY'; requestId: string; missing: string[] }
  /** Log-only — nothing to send. */
  | { type: 'WARN'; message: string }
  | { type: 'NONE' };

export interface PageContextResult {
  state: PageContextMachineState;
  action: PageContextAction;
}

/**
 * Pure decision function — see the file header for the full handshake this
 * implements. Never touches a socket, a timer, or the conversation store;
 * the driver (useAssistantConnection.ts) does all of that off the actions
 * this returns.
 */
export function nextPageContextAction(
  state: PageContextMachineState,
  event: PageContextEvent
): PageContextResult {
  switch (event.type) {
    case 'SOCKET_OPENED': {
      // REJECTED is scoped to the whole page load, not the socket — a
      // reconnect must not retry a payload the server already told us is
      // malformed (only a genuinely NEW payload, via PAYLOAD_ARRIVED, does).
      if (state.phase === PageContextPhase.REJECTED) {
        return { state, action: { type: 'NONE' } };
      }
      if (!event.hasPayload) {
        return { state: INITIAL_PAGE_CONTEXT_STATE, action: { type: 'NONE' } };
      }
      return {
        state: {
          phase: PageContextPhase.HEAD_SENT,
          requestId: event.requestId,
          missing: [],
        },
        action: { type: 'SEND_HEAD', requestId: event.requestId },
      };
    }

    case 'PAYLOAD_ARRIVED': {
      // A new payload always gets a fresh attempt, even out of REJECTED —
      // unlike SOCKET_OPENED, this event means the host actually re-extracted
      // (new page, or a corrected one), not just a reconnect on the old one.
      if (!event.socketOpen) {
        return { state: INITIAL_PAGE_CONTEXT_STATE, action: { type: 'NONE' } };
      }
      return {
        state: {
          phase: PageContextPhase.HEAD_SENT,
          requestId: event.requestId,
          missing: [],
        },
        action: { type: 'SEND_HEAD', requestId: event.requestId },
      };
    }

    case 'ACK_RECEIVED': {
      if (event.requestId !== state.requestId) {
        // A superseded request's ack (e.g. a payload replaced mid-flight, or
        // this landed after we'd already moved on) — never allowed to move
        // the machine. Logged so a real backend bug doesn't look like silence.
        return {
          state,
          action: {
            type: 'WARN',
            message: `late page_context_ack ignored (request_id mismatch, status=${event.status})`,
          },
        };
      }

      if (event.status === 'cached' || event.status === 'stored') {
        return {
          state: { ...state, phase: PageContextPhase.ACKED, missing: [] },
          action: { type: 'NONE' },
        };
      }

      if (event.status === 'send_body') {
        // Also the WS-D.7 path: a LATER send_body under the ORIGINAL
        // request_id (a section expired from the server's cache) — this
        // branch handles both the initial ask and that re-ask identically.
        const missing = event.missing ?? [];
        return {
          state: { ...state, phase: PageContextPhase.BODY_SENT, missing },
          action: { type: 'SEND_BODY', requestId: event.requestId, missing },
        };
      }

      if (event.status === 'disabled') {
        return {
          state: { ...state, phase: PageContextPhase.UNSUPPORTED },
          action: {
            type: 'WARN',
            message: `page context disabled (${event.reason ?? 'unknown reason'})`,
          },
        };
      }

      // 'rejected'
      return {
        state: { ...state, phase: PageContextPhase.REJECTED },
        action: {
          type: 'WARN',
          message: `page context rejected (${event.reason ?? 'unknown reason'})`,
        },
      };
    }

    case 'ACK_TIMEOUT': {
      // Only meaningful while actually awaiting an ack — a stray/duplicate
      // timer firing after the handshake already resolved is a no-op.
      if (
        state.phase !== PageContextPhase.HEAD_SENT &&
        state.phase !== PageContextPhase.BODY_SENT
      ) {
        return { state, action: { type: 'NONE' } };
      }
      return {
        state: { ...state, phase: PageContextPhase.UNSUPPORTED },
        action: { type: 'WARN', message: 'page_context_ack timed out' },
      };
    }

    case 'SOCKET_CLOSED': {
      // REJECTED survives the close (page-load-scoped); everything else
      // resets so the next open starts a clean handshake.
      if (state.phase === PageContextPhase.REJECTED) {
        return { state, action: { type: 'NONE' } };
      }
      return { state: INITIAL_PAGE_CONTEXT_STATE, action: { type: 'NONE' } };
    }
  }
}
